import React, { useEffect, useState, useCallback } from "react";
import Header from "./Header";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../server";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(n)) ? Number(n) : 0);

const statusColor = (s) =>
  ({
    Ordered: "bg-secondary-subtle text-secondary",
    Accepted: "bg-info-subtle text-info",
    Picked: "bg-warning-subtle text-warning",
    Delivered: "bg-success-subtle text-success",
    Cancelled: "bg-danger-subtle text-danger",
  })[s] || "bg-light text-dark";

const paymentModeLabel = (m) =>
  ({
    credit: "On Credit",
    advance: "Advance Payment",
    cod: "Cash on Delivery",
  })[m] ||
  m ||
  "COD";

const paymentModeBadge = (m) =>
  ({
    credit: "bg-purple-subtle text-purple",
    advance: "bg-info-subtle text-info",
    cod: "bg-secondary-subtle text-secondary",
  })[m] || "bg-secondary-subtle text-secondary";

// ── Items Card ────────────────────────────────────────────────────────────────
function ItemsCard({ items = [], totalAmount }) {
  return (
    <div className="card border-0 shadow-sm rounded-4 mb-2">
      <div className="card-body p-3 p-md-4">
        {/* header */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
            style={{ width: 40, height: 40 }}
          >
            <i className="bi bi-box-seam fs-5" />
          </div>
          <div>
            <div className="fw-semibold lh-1">Order Items</div>
            <div className="text-secondary small">
              {items.length} item{items.length !== 1 ? "s" : ""} in this order
            </div>
          </div>
        </div>

        {/* item rows */}
        <div className="d-flex flex-column gap-2 mb-3">
          {items.map((it, i) => (
            <div
              key={i}
              className="d-flex align-items-center gap-3 rounded-3 px-3 py-2"
              style={{
                background: "var(--bs-light, #f8f9fa)",
                border: "1px solid #e9ecef",
              }}
            >
              {/* product icon */}
              <div
                className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 bg-white"
                style={{
                  width: 44,
                  height: 44,
                  border: "1px solid #e0e0e0",
                  fontSize: 22,
                }}
              >
                💧
              </div>

              {/* name + unit price */}
              <div className="flex-grow-1 min-w-0">
                <div
                  className="fw-semibold text-truncate"
                  style={{ fontSize: 14 }}
                >
                  {it.productName}
                </div>
                <div className="text-secondary" style={{ fontSize: 12 }}>
                  {money(it.unitPrice)} per unit
                </div>
              </div>

              {/* qty badge */}
              <div
                className="text-center flex-shrink-0"
                style={{ minWidth: 50 }}
              >
                <div
                  className="badge rounded-pill bg-primary-subtle text-primary fw-semibold"
                  style={{ fontSize: 13, padding: "5px 12px" }}
                >
                  × {it.quantity}
                </div>
                <div
                  className="text-secondary"
                  style={{ fontSize: 10, marginTop: 3 }}
                >
                  qty
                </div>
              </div>

              {/* line total */}
              <div className="text-end flex-shrink-0" style={{ minWidth: 64 }}>
                <div className="fw-bold" style={{ fontSize: 15 }}>
                  {money(it.totalPrice)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* total footer */}
        <div
          className="d-flex justify-content-between align-items-center rounded-3 px-3 py-2"
          style={{ background: "#1e3a5f", color: "#fff" }}
        >
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            {items.reduce((s, i) => s + i.quantity, 0)} unit
            {items.reduce((s, i) => s + i.quantity, 0) !== 1 ? "s" : ""} total
          </div>
          <div>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              Order Total&nbsp;&nbsp;
            </span>
            <span style={{ fontSize: 18, fontWeight: 700 }}>
              {money(totalAmount)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Driver Card ───────────────────────────────────────────────────────────────
function DriverCard({
  drv,
  drivers,
  selectedDriver,
  setSelectedDriver,
  canAssign,
  busy,
  onAssign,
}) {
  const initials = drv
    ? drv.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : null;

  // active order count — shown from driver data if available
  const activeOrders = drv?.activeOrders ?? null;

  return (
    <div className="card border-0 shadow-sm rounded-4 mb-2">
      <div className="card-body p-3 p-md-4">
        {/* header */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning"
            style={{ width: 40, height: 40 }}
          >
            <i className="bi bi-truck fs-5" />
          </div>
          <div className="flex-grow-1">
            <div className="fw-semibold lh-1">Delivery Driver</div>
            <div className="text-secondary small">Assigned for this order</div>
          </div>
          {drv && (
            <span className="badge bg-success-subtle text-success">
              <i className="bi bi-circle-fill me-1" style={{ fontSize: 7 }} />
              Assigned
            </span>
          )}
          {!drv && (
            <span className="badge bg-warning-subtle text-warning">
              <i className="bi bi-circle me-1" style={{ fontSize: 7 }} />
              Unassigned
            </span>
          )}
        </div>

        {/* driver profile */}
        {drv ? (
          <div
            className="d-flex align-items-center gap-3 rounded-3 p-3 mb-0"
            style={{
              background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)",
              border: "1px solid #c7d7f9",
            }}
          >
            {/* avatar */}
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
              style={{
                width: 52,
                height: 52,
                fontSize: 18,
                background: "#1e3a5f",
                color: "#fff",
                letterSpacing: 1,
              }}
            >
              {initials}
            </div>

            {/* info */}
            <div className="flex-grow-1 min-w-0">
              <div className="fw-semibold" style={{ fontSize: 15 }}>
                {drv.name}
              </div>
              <div
                className="d-flex align-items-center gap-1 text-secondary"
                style={{ fontSize: 13 }}
              >
                <i className="bi bi-telephone" style={{ fontSize: 11 }} />
                {drv.phone}
              </div>
              {drv.vehicleNumber && (
                <div className="d-flex align-items-center gap-1 mt-1">
                  <span
                    className="badge bg-white text-dark border"
                    style={{ fontSize: 11, letterSpacing: "0.05em" }}
                  >
                    <i className="bi bi-truck me-1" style={{ fontSize: 10 }} />
                    {drv.vehicleNumber}
                  </span>
                  {activeOrders !== null && (
                    <span
                      className="badge bg-info-subtle text-info border-0"
                      style={{ fontSize: 11 }}
                    >
                      {activeOrders} active order{activeOrders !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className="d-flex flex-column align-items-center justify-content-center rounded-3 py-4 mb-0"
            style={{ background: "#fffbeb", border: "1.5px dashed #fcd34d" }}
          >
            <div style={{ fontSize: 32 }}>🚚</div>
            <div
              className="fw-semibold text-warning mt-2"
              style={{ fontSize: 14 }}
            >
              No driver assigned yet
            </div>
            <div
              className="text-secondary text-center"
              style={{ fontSize: 12, maxWidth: 200, marginTop: 4 }}
            >
              {canAssign
                ? "Use the selector below to assign a driver."
                : "This order is closed."}
            </div>
          </div>
        )}

        {/* assign / re-assign section */}
        {canAssign && (
          <div className="mt-3 pt-3 border-top">
            <label
              className="form-label fw-semibold mb-2"
              style={{ fontSize: 13 }}
            >
              {drv ? "Re-assign to another driver" : "Assign a driver"}
            </label>
            <select
              className="form-select mb-2"
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="">Select driver...</option>
              {drivers.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name} • {d.phone} • {d.vehicleNumber}
                </option>
              ))}
            </select>
            <button
              className="btn w-100 fw-medium"
              style={{
                background: "#1e3a5f",
                color: "#fff",
                borderRadius: 8,
                fontSize: 13,
              }}
              onClick={onAssign}
              disabled={busy || !selectedDriver}
            >
              {busy ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : (
                <i className="bi bi-person-check me-2" />
              )}
              {drv ? "Re-assign Driver" : "Assign Driver"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Payment Ledger Card ───────────────────────────────────────────────────────
function PaymentLedgerCard({ order }) {
  const isAdvance = order.paymentMode === "advance";
  const isCredit = order.paymentMode === "credit";
  const isCorp = order.orderType === "corporate";

  const amountPaid = Number(order.amountPaid || 0);
  const total = Number(order.totalAmount || 0);
  const amountDue = Math.max(0, total - amountPaid);
  const pct =
    total > 0 ? Math.min(100, Math.round((amountPaid / total) * 100)) : 0;
  const ledger = order.paymentLedger || [];
  const fullPaid = amountDue <= 0;

  return (
    <div className="card border-0 shadow-sm rounded-4">
      <div className="card-body p-3 p-md-4">
        {/* header */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            className="rounded-3 d-inline-flex align-items-center justify-content-center bg-secondary-subtle text-secondary"
            style={{ width: 40, height: 40 }}
          >
            <i className="bi bi-credit-card fs-5" />
          </div>
          <div>
            <div className="fw-semibold lh-1">Payment</div>
            <div className="text-secondary small">
              {isCorp
                ? paymentModeLabel(order.paymentMode)
                : "Cash on Delivery"}
            </div>
          </div>
          <span
            className={`badge ms-auto ${paymentModeBadge(order.paymentMode)}`}
          >
            {paymentModeLabel(order.paymentMode)}
          </span>
        </div>

        {/* amount summary */}
        <div className="row g-2 mb-3">
          <div className="col-4 text-center">
            <div className="fw-bold fs-6">{money(total)}</div>
            <div className="text-secondary" style={{ fontSize: 11 }}>
              Order Total
            </div>
          </div>
          <div className="col-4 text-center border-start border-end">
            <div className="fw-bold fs-6 text-success">{money(amountPaid)}</div>
            <div className="text-secondary" style={{ fontSize: 11 }}>
              Amount Paid
            </div>
          </div>
          <div className="col-4 text-center">
            <div
              className={`fw-bold fs-6 ${fullPaid ? "text-success" : "text-danger"}`}
            >
              {money(amountDue)}
            </div>
            <div className="text-secondary" style={{ fontSize: 11 }}>
              Balance Due
            </div>
          </div>
        </div>

        {/* progress bar */}
        {isCorp && (isAdvance || isCredit) && (
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-secondary">Payment Progress</small>
              <small
                className={
                  fullPaid ? "text-success fw-semibold" : "text-secondary"
                }
              >
                {fullPaid ? "✅ Fully Paid" : `${pct}% paid`}
              </small>
            </div>
            <div className="progress rounded-pill" style={{ height: 8 }}>
              <div
                className={`progress-bar rounded-pill ${fullPaid ? "bg-success" : "bg-primary"}`}
                style={{ width: `${pct}%`, transition: "width 0.6s ease" }}
              />
            </div>
          </div>
        )}

        {/* ledger */}
        {isAdvance && ledger.length > 0 && (
          <>
            <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
              <i className="bi bi-list-ol me-1 text-primary" />
              Payment History
            </div>
            <div
              className="rounded-3 overflow-hidden"
              style={{ border: "1px solid #e9ecef" }}
            >
              <div
                className="d-flex px-3 py-2 text-secondary"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  background: "#f8f9fa",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                <span style={{ width: 28 }}>#</span>
                <span className="flex-grow-1">Date & Note</span>
                <span>Amount</span>
              </div>
              {ledger.map((entry, i) => {
                const d = new Date(entry.paidAt);
                const dateStr = d.toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                });
                const timeStr = d.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const isFirst = i === 0;
                return (
                  <div
                    key={entry._id || i}
                    className={`d-flex align-items-center px-3 py-2 ${i < ledger.length - 1 ? "border-bottom" : ""}`}
                    style={{ background: isFirst ? "#f0fdf4" : "#fff" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle me-2 flex-shrink-0"
                      style={{
                        width: 22,
                        height: 22,
                        background: isFirst ? "#16a34a" : "#1e3a5f",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                      <div className="fw-semibold" style={{ fontSize: 13 }}>
                        {dateStr}
                        <span
                          className="text-secondary fw-normal ms-2"
                          style={{ fontSize: 11 }}
                        >
                          {timeStr}
                        </span>
                      </div>
                      <div
                        className="text-secondary text-truncate"
                        style={{ fontSize: 11 }}
                      >
                        {isFirst &&
                        entry.note?.toLowerCase().includes("advance")
                          ? "💰 Advance paid at order placement"
                          : entry.note || "Payment"}
                      </div>
                    </div>
                    <div
                      className={`fw-bold flex-shrink-0 ${isFirst ? "text-success" : "text-primary"}`}
                      style={{ fontSize: 14 }}
                    >
                      {money(entry.amount)}
                    </div>
                  </div>
                );
              })}
              <div
                className="d-flex justify-content-between align-items-center px-3 py-2 border-top"
                style={{ background: "#f8f9fa" }}
              >
                <span className="text-secondary" style={{ fontSize: 12 }}>
                  {ledger.length} payment{ledger.length !== 1 ? "s" : ""}
                </span>
                <div className="text-end">
                  <span
                    className="fw-bold text-success me-3"
                    style={{ fontSize: 13 }}
                  >
                    Paid: {money(amountPaid)}
                  </span>
                  {!fullPaid && (
                    <span
                      className="fw-bold text-danger"
                      style={{ fontSize: 13 }}
                    >
                      Due: {money(amountDue)}
                    </span>
                  )}
                  {fullPaid && (
                    <span className="badge bg-success-subtle text-success">
                      🎉 Fully Paid
                    </span>
                  )}
                </div>
              </div>
            </div>
            {!fullPaid && (
              <div className="alert alert-warning rounded-3 d-flex align-items-center gap-2 py-2 px-3 mt-3 mb-0">
                <i className="bi bi-exclamation-triangle-fill flex-shrink-0" />
                <div style={{ fontSize: 13 }}>
                  Outstanding balance of <strong>{money(amountDue)}</strong>{" "}
                  remaining. Customer can pay from Order History in the app.
                </div>
              </div>
            )}
          </>
        )}

        {isCredit && isCorp && (
          <div
            className="alert alert-info rounded-3 d-flex align-items-center gap-2 py-2 px-3 mb-0"
            style={{ fontSize: 13 }}
          >
            <i className="bi bi-info-circle-fill flex-shrink-0" />
            <div>
              This order is billed on <strong>credit</strong>. Full amount of{" "}
              <strong>{money(total)}</strong> will be settled later.
            </div>
          </div>
        )}

        {!isCorp && (
          <div className="d-flex justify-content-between align-items-center pt-1">
            <span className="text-secondary">Total</span>
            <span className="fw-bold fs-5">{money(total)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/admin/orders/${id}`);
      setOrder(data?.order || null);
      setSelectedDriver(data?.order?.driver?._id || "");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchDrivers = useCallback(async () => {
    try {
      const data = await api.get("/api/admin/drivers");
      setDrivers((data?.drivers || []).filter((d) => d.isActive));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchOrder();
    fetchDrivers();
  }, [fetchOrder, fetchDrivers]);

  const assignDriver = async () => {
    if (!selectedDriver) return alert("Choose a driver");
    setBusy(true);
    try {
      await api.put(`/api/admin/orders/${id}/assign`, {
        driver_id: selectedDriver,
      });
      await fetchOrder();
    } catch (err) {
      alert(err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async () => {
    const reason = window.prompt("Cancellation reason (optional):", "");
    if (reason === null) return;
    if (!window.confirm("Cancel this order?")) return;
    setBusy(true);
    try {
      await api.put(`/api/admin/orders/${id}/cancel`, { reason });
      await fetchOrder();
    } catch (err) {
      alert(err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div id="db-wrapper">
          <main id="page-content">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Header />
        <div id="db-wrapper">
          <main id="page-content">
            <div className="container-fluid p-4">
              <div className="alert alert-warning">Order not found</div>
              <Link to="/orders" className="btn btn-light border">
                ← Back
              </Link>
            </div>
          </main>
        </div>
      </>
    );
  }

  const cust = order.customer || {};
  const drv = order.driver || null;
  const addr = order.deliveryAddress || {};
  const isCorp = order.orderType === "corporate";
  const canCancel = !["Delivered", "Cancelled"].includes(order.status);
  const canAssign = !["Delivered", "Cancelled"].includes(order.status);

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            {/* title bar */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-light border rounded-3"
                  onClick={() => navigate(-1)}
                >
                  <i className="bi bi-arrow-left" />
                </button>
                <div>
                  <h3 className="mb-0 lh-1 ls-sm fw-normal">
                    Order {order.orderNumber}
                    <span className={`badge ms-2 ${statusColor(order.status)}`}>
                      {order.status}
                    </span>
                    {isCorp && (
                      <span className="badge ms-2 bg-info-subtle text-info">
                        Corporate
                      </span>
                    )}
                  </h3>
                  <div className="text-secondary lh-1 mt-1">
                    {order.formattedDate}
                  </div>
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  className="btn btn-light border px-4"
                  onClick={() => window.print()}
                >
                  <i className="bi bi-printer me-2" />
                  Print
                </button>
                {canCancel && (
                  <button
                    className="btn btn-danger px-4"
                    onClick={cancelOrder}
                    disabled={busy}
                  >
                    <i className="bi bi-x-lg me-2" />
                    Cancel Order
                  </button>
                )}
              </div>
            </div>

            <div className="row g-2">
              {/* LEFT */}
              <div className="col-lg-8">
                {/* Items — new card */}
                <ItemsCard
                  items={order.items}
                  totalAmount={order.totalAmount}
                />

                {/* Timeline */}
                <div className="card border-0 shadow-sm rounded-4 mb-2">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                        style={{ width: 40, height: 40 }}
                      >
                        <i className="bi bi-clock-history" />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">Order Timeline</div>
                        <div className="text-secondary small">
                          Status transitions and timestamps
                        </div>
                      </div>
                    </div>
                    <ul className="list-unstyled mb-0">
                      {[
                        {
                          label: "Ordered",
                          at: order.orderedAt,
                          icon: "bi-cart-check",
                          color: "text-secondary",
                        },
                        {
                          label: "Accepted",
                          at: order.acceptedAt,
                          icon: "bi-hand-thumbs-up",
                          color: "text-info",
                        },
                        {
                          label: "Picked",
                          at: order.pickedAt,
                          icon: "bi-truck",
                          color: "text-warning",
                        },
                        {
                          label: "Delivered",
                          at: order.deliveredAt,
                          icon: "bi-check2-circle",
                          color: "text-success",
                        },
                        {
                          label: "Cancelled",
                          at: order.cancelledAt,
                          icon: "bi-x-circle",
                          color: "text-danger",
                        },
                      ]
                        .filter((s) => s.at)
                        .map((s, i) => (
                          <li key={i} className="d-flex align-items-start mb-2">
                            <div className={`me-3 ${s.color}`}>
                              <i className={`bi ${s.icon} fs-5`} />
                            </div>
                            <div>
                              <div className="fw-semibold">{s.label}</div>
                              <small className="text-secondary">
                                {new Date(s.at).toLocaleString()}
                              </small>
                            </div>
                          </li>
                        ))}
                    </ul>
                    {order.cancellationReason && (
                      <div className="alert alert-warning mt-3 mb-0 rounded-3">
                        <strong>Reason:</strong> {order.cancellationReason}
                      </div>
                    )}
                    {order.notes && (
                      <div className="mt-3">
                        <strong>Customer note:</strong> {order.notes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment */}
                <PaymentLedgerCard order={order} />
              </div>

              {/* RIGHT */}
              <div className="col-lg-4">
                {/* Customer */}
                <div className="card border-0 shadow-sm rounded-4 mb-2">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-secondary-subtle text-secondary"
                        style={{ width: 32, height: 32 }}
                      >
                        <i className="bi bi-person" />
                      </div>
                      <div className="text-secondary small text-uppercase">
                        Customer
                      </div>
                    </div>
                    <div className="fw-semibold">{cust.name}</div>
                    <div className="small text-secondary">{cust.phone}</div>
                    {cust.email && (
                      <div className="small text-secondary">{cust.email}</div>
                    )}
                    <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className={`badge ${cust.customerType === "corporate" ? "bg-info-subtle text-info" : "bg-secondary-subtle text-secondary"}`}
                      >
                        {cust.customerType || "individual"}
                      </span>
                      {cust.companyName && (
                        <span className="small text-secondary">
                          {cust.companyName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Delivery address */}
                <div className="card border-0 shadow-sm rounded-4 mb-2">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-secondary-subtle text-secondary"
                        style={{ width: 32, height: 32 }}
                      >
                        <i className="bi bi-geo-alt" />
                      </div>
                      <div className="text-secondary small text-uppercase">
                        Delivery Address
                      </div>
                    </div>
                    <div className="fw-semibold">{addr.label || "—"}</div>
                    <div className="small text-secondary">
                      {addr.addressLine}
                    </div>
                    <div className="small text-secondary">
                      {addr.city} — {addr.pincode}
                    </div>
                  </div>
                </div>

                {/* Driver — new card */}
                <DriverCard
                  drv={drv}
                  drivers={drivers}
                  selectedDriver={selectedDriver}
                  setSelectedDriver={setSelectedDriver}
                  canAssign={canAssign}
                  busy={busy}
                  onAssign={assignDriver}
                />
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default OrderDetails;
